import { SetStateAction, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { getAuctionEventById } from "services";
import { v4 as uuidv4 } from "uuid";
import CustomRejectionModal from "./../CustomRejectionModal";
import CustomErrorHandlingModal from "./../CustomErrorHandlingModal";
import { DateTime } from "luxon";
import BankerPageNotFound from "../../BankerPageNotFound";
import {
  bidderDataInitialise,
  useGetBasicDetails,
  getTeamMembersEmailDetails,
  handleAcceptBidder,
  handleBidderDataSubmit,
  handleRejectBidder,
  handlePANFieldValidations,
  focusFieldById,
  kycDetailsSwitchFields,
  useInitializeBetaForms,
} from "../bidderFormServices/getDetails";
import {
  AcceptRejectComponent,
  BidderDocumentUploader,
  BidderFormFieldsHelper,
  BidderPDFViewer,
  NotificationModal,
  SubmitButtonComponent,
} from "../bidderFormServices/BidderFormFieldsHelper";
import { getIndividualFormFieldsConfig } from "./IndividualBidderFormConfig";
import { Button } from "../../../../shadcn/components/ui/button";
import { validatePaymentRequest } from "services/bidder";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

interface BidderFormProps {
  mode?: string;
  allBiddersData?: any;
  currentBidderData?: any;
  activeStep?: number;
  setActiveStep?: React.Dispatch<SetStateAction<number>>;
  bidderFormWrapperClassName?: string;
  setCurrentBidderData?: React.Dispatch<SetStateAction<Array<any>>>;
}
export const IndividualBidderForm = ({
  mode,
  bidderFormWrapperClassName,
}: BidderFormProps) => {
  const router = useRouter();
  const { for_event } = router.query;
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const [bidderData, setBidderData] = useState<any>();
  const [membersEmail, setMembersEmail] = useState<any>([]);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [jointDeclarationFiles, setJointDeclarationFiles] = useState<any>([]);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [randomId, setRandomId] = useState<any>();
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  const [disablePan, setDisablePan] = useState(false);
  const [notificationPopup, setNotificationPopup] = useState("");
  type ErrorType = "validation" | "api";
  const [isErrorHandlingModalOpen, setIsErrorHandlingModalOpen] = useState<{
    open: boolean;
    type: ErrorType;
  }>({
    open: false,
    type: "validation",
  });
  const [errorHandlingModalData, setErrorHandlingModalData] =
    useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isOverwrite, setIsOverwrite] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { banker, currentBranch, bank } = useGetBasicDetails();

  // V4 name-first flow: debounce ref for PAN verification trigger
  const panVerificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced function to trigger PAN verification when name >= 3 chars
  const triggerPanVerification = () => {
    if (panVerificationTimeoutRef.current) {
      clearTimeout(panVerificationTimeoutRef.current);
    }
    panVerificationTimeoutRef.current = setTimeout(() => {
      const nameValue = form?.getField?.("name");
      const panValue = form?.getField?.("pan");

      // Only trigger if name >= 3 AND PAN has value
      if (
        nameValue &&
        nameValue.length >= 3 &&
        panValue &&
        panValue.length >= 10 &&
        isDigioPanFullVerificationEnabled
      ) {
        handlePANFieldValidations(
          { target: { value: panValue } },
          form,
          setIsFetchingPan,
          setIsNameVerified,
        );
      }
    }, 500);
  };

  useEffect(() => {
    if ((id && mode === "view") || mode === "edit") {
      (async () => {
        await bidderinit(id);
      })();
    }
    if (for_event) {
      (async () => {
        await init(for_event);
      })();
    }

    getTeamMembersEmailDetails(currentBranch, setMembersEmail);
  }, [for_event, id]);

  useEffect(() => {
    if (mode === "create") setRandomId(uuidv4());
  }, []);

  const init = async (id: any) => {
    const data: any = await getAuctionEventById(id);
    setEvent(data);
  };

  const bidderinit = async (id: any) => {
    const data = await bidderDataInitialise(
      id,
      setBidderData,
      setKycFiles,
      setJointDeclarationFiles,
      setRenderKey,
    );
    if (!data) {
      setNotFound(true);
    }
  };

  const form = useInitializeBetaForms(
    bidderData,
    membersEmail,
    mode ?? "view",
    setIsNameVerified,
    "individual",
  );

  const individualFormFieldsConfig = getIndividualFormFieldsConfig(
    form,
    mode,
    setIsFetchingPan,
    setIsNameVerified,
    isNameVerified,
    disablePan,
    setDisablePan,
    setNotificationPopup,
    triggerPanVerification,
  );

  // Keep `initial_bid_price_confirmed` in sync with saved DB value in edit mode.
  useEffect(() => {
    const initialBid = form.value?.initial_bid_price;
    console.log("🚀 ~ IndividualBidderForm ~ initialBid:", initialBid);

    if (
      initialBid === "" ||
      initialBid === null ||
      typeof initialBid === "undefined"
    ) {
      form.setField("initial_bid_price_confirmed", false);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    }

    if (mode === "edit") {
      const saved = bidderData?.meta?.initial_bid_price;
      const savedNum = typeof saved === "string" ? Number(saved) : saved;
      const shouldConfirm =
        savedNum !== undefined &&
        !isNaN(savedNum) &&
        Number(initialBid) === Number(savedNum);
      console.log("🚀 ~ IndividualBidderForm ~ shouldConfirm:", shouldConfirm);
      form.setField("initial_bid_price_confirmed", shouldConfirm);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    } else {
      // creation or no bidderData: leave confirmation false until user checks
      form.setField("initial_bid_price_confirmed", false);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    }
  }, [form.value?.initial_bid_price, bidderData, mode]);

  const handleSubmit = async () => {
    const initialBid = form.getField("initial_bid_price");
    const confirmed = form.getField("initial_bid_price_confirmed");

    if (
      initialBid !== "" &&
      initialBid !== null &&
      typeof initialBid !== "undefined"
    ) {
      const reservePrice =
        event?.reserve_price ?? event?.property_dump?.reserve_price;
      if (
        reservePrice !== undefined &&
        reservePrice !== null &&
        reservePrice !== ""
      ) {
        if (Number(initialBid) < Number(reservePrice)) {
          form.pushError({
            initial_bid_price: [
              `Initial Bid Price must be greater than or equal to the Reserve Price (₹${Number(
                reservePrice,
              ).toLocaleString("en-IN")}).`,
            ],
          });
          try {
            focusFieldById("initial_bid_price");
          } catch (e) {
            /* ignore */
          }
          return;
        }
      }

      if (!confirmed) {
        form.pushError({
          initial_bid_price_confirmed: [
            "Please tick the checkbox below the offer price field to confirm the amount.",
          ],
        });
        try {
          focusFieldById("initial_bid_price_confirmed");
        } catch (e) {
          /* ignore */
        }
        return;
      }
    }

    await handleBidderDataSubmit(
      form,
      mode,
      id,
      for_event,
      banker,
      bank,
      currentBranch,
      setIsErrorHandlingModalOpen,
      setErrorHandlingModalData,
      randomId,
      kycFiles,
      isOverwrite,
      router,
    );
  };

  const handleEMDPaymentAcceptAction = async (
    type: "approved" | "rejected",
  ) => {
    try {
      if (type === "approved") {
        const payload = {
          event_uuid: bidderData?.event_uuid,
          approver_user_uuid: banker.uuid,
          approver_email: banker?.attributes?.find(
            (a: any) => a.type === "email",
          )?.value,
          approver_name: banker?.profile?.bio?.full_name,
          bidder_uuid: bidderData.uuid,
          action_by: "banker",
          approval_status: "approved",
          remarks: "",
        };
        await validatePaymentRequest(payload);
      }
      if (type === "rejected") {
        const payload = {
          event_uuid: bidderData?.event_uuid,
          approver_user_uuid: banker.uuid,
          approver_email: banker?.attributes?.find(
            (a: any) => a.type === "email",
          )?.value,
          action_by: "banker",
          approver_name: banker?.profile?.bio?.full_name,
          bidder_uuid: bidderData.uuid,
          approval_status: "rejected",
          remarks: "",
        };
        await validatePaymentRequest(payload);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      window.location.reload();
    }
  };

  if (notFound) return <BankerPageNotFound />;

  return (
    <>
      {
        <div
          className={
            bidderFormWrapperClassName ??
            "lg:pl-9 lg:py-[41px] lg:pr-[70px] space-y-8 lg:space-y-7 lg:mr-[88px] mt-9 lg:mt-6 lg:bg-brand-bgLightGrey"
          }
        >
          {individualFormFieldsConfig?.map((field) => (
            <BidderFormFieldsHelper
              key={field.id}
              {...field}
              mode={mode}
              isFetchingPan={isFetchingPan}
            />
          ))}

          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-y-6 mt-3 lg:mt-0  lg:gap-y-5 ">
            {kycDetailsSwitchFields?.map((item: any, index) => (
              <BidderFormFieldsHelper
                key={index}
                id={`asset-${index}`}
                formFieldType="switch_field"
                fieldLabel={item?.title}
                switchFieldValue={
                  form.getField(item?.value) !== ""
                    ? form.getField(item?.value)
                    : false
                }
                setSwitchFieldValue={(checked: boolean) =>
                  form.setField(item?.value, checked)
                }
                disableConditions={mode === "view"}
                mode={mode}
              />
            ))}
          </div>

          <BidderDocumentUploader
            hideConditions={mode === "create" || mode === "edit" ? false : true}
            property_uuid={
              mode === "edit" && typeof id === "string" ? id : randomId
            }
            owner_service_type="kyc-documents"
            dataFiles={kycFiles}
            setDataFiles={setKycFiles}
            allowedMimeTypes={["application/pdf", "image/png"]}
            renderKey={renderKey}
            headingText="Upload KYC documents"
            disableConditions={
              mode === "create" || bidderData?.created_by === "banker"
                ? false
                : true
            }
          />
          {mode === "view" &&
            bidderData?.created_by === "user" &&
            bidderData?.payment_data?.emd_proof_uuid?.[0]?.id && (
              <div className="my-6">
                <div className="font-openSans text-md font-semibold">
                  EMD Payment Proof
                </div>
                <div className="text-sm py-4">
                  <div className="rounded-lg py-3 inline-block">
                    <span className="mr-2 font-medium">
                      EMD Payment Status:
                    </span>
                    <span
                      className={`font-semibold capitalize ${
                        bidderData?.payment_data?.approval_status === "approved"
                          ? "text-brand-bgSuccessGreen"
                          : bidderData?.payment_data?.approval_status ===
                            "rejected"
                          ? "text-brand-textRed"
                          : "text-brand-textBlack"
                      }`}
                    >
                      {bidderData?.payment_data?.approval_status}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="text-md text-brand-textBlack mb-3">
                    Payment Details
                  </h4>
                  <div className="space-y-2 text-brand-textBlack text-sm pb-4 grid md:grid-cols-2 px-4">
                    <div>
                      <span className="font-medium mr-2">Transaction Id:</span>{" "}
                      {bidderData?.payment_data?.transaction_id}
                    </div>
                    <div>
                      <span className="font-medium mr-2">Amount:</span> ₹{" "}
                      {bidderData?.payment_data?.transaction_amount}
                    </div>
                    <div>
                      <span className="font-medium mr-2">Bank:</span>{" "}
                      {bidderData?.payment_data?.emd_paid_from}
                    </div>
                    <div>
                      <span className="font-medium mr-2">Submission Date:</span>{" "}
                      {bidderData?.payment_data?.emd_paid_date
                        ? DateTime.fromISO(
                            bidderData.payment_data.emd_paid_date,
                          )
                            .setZone("Asia/Kolkata")
                            .toFormat("dd MMM yyyy")
                        : ""}
                    </div>
                  </div>
                </div>
                <div className="border-b mb-2">
                  <h4 className="text-md text-brand-textBlack mb-3">
                    Receiver Details
                  </h4>
                  <div className="space-y-2 text-brand-textBlack text-sm pb-4 px-4 grid md:grid-cols-2">
                    <div>
                      <span className="font-medium text-brand-orange mr-2">
                        Receiver Name:
                      </span>
                      <span className="capitalize text-sm tracking-wide">
                        {bidderData?.payment_data?.approver_name || ""}
                      </span>
                    </div>

                    <div>
                      <span className="font-medium text-brand-orange mr-2">
                        Receiver Email:
                      </span>
                      {bidderData?.payment_data?.approver_email}
                    </div>

                    <div className="capitalize">
                      <span className="font-medium text-brand-orange mr-2">
                        Receiver Mobile Number:
                      </span>
                      {bidderData?.payment_data?.approver_mobile || ""}
                    </div>

                    <div>
                      <span className="font-medium text-brand-orange mr-2">
                        Payment Received Date:
                      </span>
                      {bidderData?.payment_data?.approval_date || ""}
                    </div>
                  </div>
                </div>
                <div className="relative py-2">
                  <div className="absolute top-0 right-0 flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-brand-headingBlue"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                      />
                    </svg>
                    <a
                      href={`${`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bidderData?.payment_data?.emd_proof_uuid?.[0]?.id}`}?dl=demand-draft-${Date.now()}.jpg`}
                      // target="_blank"
                      // rel="noopener noreferrer"
                      download={`demand-draft-${Date.now()}.jpg`}
                      className="inline-flex text-sm tracking-wide font-openSans items-center text-brand-headingBlue hover:underline"
                    >
                      Download
                    </a>
                  </div>
                  <div className="flex justify-center md:justify-end mt-4 ">
                    <img
                      src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bidderData?.payment_data?.emd_proof_uuid?.[0]?.id}`}
                      alt="Demand Draft"
                      className="w-full h-auto max-h-[370px] min-h-[150px] object-contain"
                    />
                  </div>
                </div>
                {bidderData?.payment_data?.approval_status === "submitted" && (
                  <div className="flex justify-center gap-10 py-4">
                    <div className="space-x-3 md:space-x-3 w-full">
                      <Button
                        onClick={async () => {
                          await handleEMDPaymentAcceptAction("approved");
                        }}
                        className={`px-5 py-3 w-full`}
                      >
                        Approve EMD Payment
                      </Button>
                    </div>
                    <div className="space-x-3 md:space-x-3 w-full">
                      <Button
                        variant="ghost"
                        onClick={async () => {
                          await handleEMDPaymentAcceptAction("rejected");
                        }}
                        className={`px-5 py-3 w-full border`}
                      >
                        Reject EMD Payment
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

          <BidderPDFViewer
            hideConditions={mode !== "view"}
            renderKey={renderKey}
            dataFiles={kycFiles}
            setDataFiles={setKycFiles}
            allowedMimeTypes={["application/pdf"]}
            property_uuid={typeof id === "string" ? id : randomId}
            owner_service_type="kyc-documents"
            headingText="View KYC documents"
          />

          <SubmitButtonComponent
            mode={mode}
            handleAcceptAction={handleSubmit}
            acceptButtonDisabled={false}
            buttonLabel={mode === "edit" ? "Update" : "Add Bidder"}
            hideConditions={mode === "view"}
          />

          <AcceptRejectComponent
            mode={mode}
            hideConditions={
              mode !== "view" || bidderData?.created_by !== "user"
            }
            acceptButtonDisabled={
              isLoading ||
              bidderData?.status === "verified" ||
              bidderData?.status === "rejected"
            }
            handleAcceptAction={() => {
              if (
                bidderData?.payment_data?.approval_status === "rejected" &&
                bidderData?.payment_data?.emd_proof_uuid?.[0]?.id
              ) {
                alert(
                  "EMD payment was rejected by the approver. Please contact Auction Bazaar support. You cannot proceed further.",
                );
                return;
              }
              if (
                bidderData?.payment_data?.approval_status !== "approved" &&
                bidderData?.payment_data?.emd_proof_uuid?.[0]?.id
              ) {
                alert(
                  "EMD Payment Require Approval. Please review or approve as AO.",
                );
                return;
              }
              handleAcceptBidder(id);
            }}
            rejectButtonDisabled={
              isLoading ||
              bidderData?.status === "verified" ||
              bidderData?.status === "rejected"
            }
            handleRejectAction={() => {
              setShowRejectionModal(true);
            }}
          />
        </div>
      }

      {showRejectionModal && (
        <CustomRejectionModal
          handleSubmit={(reason: string) => {
            handleRejectBidder(reason, id, setIsLoading);
          }}
          modalType={"rejection"}
          onClose={setShowRejectionModal}
          isLoading={isLoading}
        />
      )}

      {notificationPopup !== "" && (
        <NotificationModal
          isOpen={notificationPopup}
          onClose={setNotificationPopup}
        />
      )}

      {isErrorHandlingModalOpen?.open && (
        <CustomErrorHandlingModal
          errorType={isErrorHandlingModalOpen?.type || "validation"}
          onClose={setIsErrorHandlingModalOpen}
          errorValue={errorHandlingModalData}
          setIsErrorHandlingModalOpen={setIsErrorHandlingModalOpen}
          setErrorHandlingModalData={setErrorHandlingModalData}
          errorHandlingModalData={errorHandlingModalData}
          handleAddBidderAnyway={handleSubmit}
          setIsOverwrite={setIsOverwrite}
        />
      )}
    </>
  );
};

export default IndividualBidderForm;
